import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { format, parseISO } from "date-fns";
import { useToast } from "../../context/ToastContext";

const IssueRequisition = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { showToast } = useToast();
  const [requisition, setRequisition] = useState(null);
  const [items, setItems]             = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchRequisition(); }, [id]);

  const fetchRequisition = async () => {
    try {
      const res = await axios.get(`/requisitions/${id}`);
      setRequisition(res.data.requisition);
      setItems(res.data.items.map((item) => {
        const approvedQuantity = Number(item.approved_quantity) || 0;
        const issuedQuantity = Number(item.issued_quantity) || 0;
        const availableStock = Number(item.available_stock) || 0;
        const remainingQuantity = Math.max(
          Number(item.remaining_quantity ?? (approvedQuantity - issuedQuantity)) || 0,
          0
        );

        return {
          ...item,
          approved_quantity: approvedQuantity,
          issued_quantity: issuedQuantity,
          available_stock: availableStock,
          remaining_quantity: remainingQuantity,
          issueNow: Math.min(remainingQuantity, availableStock),
        };
      }));
    } catch (err) {
      console.error("Error fetching requisition:", err);
    }
  };

  const handleIssueChange = (itemId, value) => {
    setItems((prev) =>
      prev.map((i) => i.item_id === itemId ? { ...i, issueNow: Number(value) } : i)
    );
  };

  const handleIssueItem = async (itemId, issuedQuantity) => {
    const item = items.find((i) => String(i.item_id) === String(itemId));
    if (!item) {
      showToast("error", "Cannot Issue", "Item not found in this requisition.");
      return;
    }

    const quantityToIssue = Number(issuedQuantity);
    const remaining = Math.max(item.approved_quantity - item.issued_quantity, 0);

    if (!Number.isInteger(quantityToIssue) || quantityToIssue <= 0 || quantityToIssue > remaining || quantityToIssue > item.available_stock) {
      showToast("error", "Cannot Issue", "Quantity exceeds available stock or remaining approved quantity.");
      return;
    }

    const user   = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id;
    setIsSubmitting(true);
    try {
      await axios.post("/requisitions/issue-requisition", {
        requisitionId: id,
        items: [{ itemId, quantity: quantityToIssue }],
        issued_by: userId,
      });
      showToast("success", "Issued", `Item issued successfully.`);
      await fetchRequisition();
      await updateRequisitionStatus();
    } catch (err) {
      showToast("error", "Failed", err.response?.data?.message || "Failed to issue item.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateRequisitionStatus = async () => {
    try {
      const res = await axios.get(`/requisitions/${id}`);
      const updatedItems = res.data.items;
      const allIssued  = updatedItems.length > 0 && updatedItems.every(
        (i) => Number(i.issued_quantity || 0) >= Number(i.approved_quantity || 0)
      );
      const someIssued = updatedItems.some((i) => Number(i.issued_quantity || 0) > 0);
      const newStatus  = allIssued ? "Issued" : someIssued ? "Partially Issued" : "Pending";
      if (newStatus !== res.data.requisition.status) {
        await axios.put(`/requisitions/${id}/update-status`, { status: newStatus });
        setRequisition((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  if (!requisition) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
    );
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Issue Requisition — #{id}</h4>
          <p>Dispense approved items from stock</p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/requisition-list")}>
          <i className="fas fa-arrow-left mr-1"></i> Back
        </button>
      </div>

      {/* ── Details ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#64748b", width: "130px", paddingBottom: "8px" }}>Requested By</td>
                    <td><strong>{requisition.user_name}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Phone</td>
                    <td>{requisition.user_phone || "—"}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Created Date</td>
                    <td>{format(parseISO(requisition.created_date), "dd/MM/yyyy")}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#64748b", width: "130px", paddingBottom: "8px" }}>Status</td>
                    <td>
                      <span className={`ims-badge ${
                        requisition.status === "Issued" ? "ims-badge-full" :
                        requisition.status === "Partially Issued" ? "ims-badge-partial" :
                        "ims-badge-approved"
                      }`}>{requisition.status}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Priority</td>
                    <td>
                      <span className={`ims-badge ${requisition.priority === "High" ? "ims-badge-rejected" : "ims-badge-principal"}`}>
                        {requisition.priority}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Expected Date</td>
                    <td>{new Date(requisition.expected_date).toLocaleDateString("en-GB")}</td>
                  </tr>
                  {requisition.req_description && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "8px" }}>Purpose</td>
                      <td>{requisition.req_description}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Items ── */}
      <div className="card shadow-sm" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
          <strong style={{ fontSize: "14px" }}>
            <i className="fas fa-box-open mr-2 text-primary"></i>Items to Issue
          </strong>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="ims-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Requested</th>
                  <th>Approved</th>
                  <th>Issued</th>
                  <th>Avail. Stock</th>
                  <th>Issue Now</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const remaining = Math.max(
                    Number(item.remaining_quantity ?? (item.approved_quantity - item.issued_quantity)) || 0,
                    0
                  );
                  const fullyDone = remaining <= 0;
                  return (
                    <tr key={item.id || `${item.requisition_id}-${item.item_id}`}>
                      <td><strong>{item.item_name}</strong></td>
                      <td>{item.requested_quantity}</td>
                      <td>{item.approved_quantity}</td>
                      <td>
                        <span style={{ color: fullyDone ? "#059669" : "#0f172a", fontWeight: 600 }}>
                          {item.issued_quantity}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: item.available_stock === 0 ? "#ef4444" : "#0f172a" }}>
                          {item.available_stock}
                        </span>
                      </td>
                      <td style={{ width: "110px" }}>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: "80px" }}
                          min="0"
                          max={Math.max(0, Math.min(remaining, item.available_stock))}
                          value={item.issueNow}
                          onChange={(e) => handleIssueChange(item.item_id, e.target.value)}
                          disabled={fullyDone}
                        />
                      </td>
                      <td>
                        {fullyDone ? (
                          <span className="ims-badge ims-badge-full" style={{ fontSize: "10px" }}>Done</span>
                        ) : (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleIssueItem(item.item_id, item.issueNow)}
                            disabled={
                              isSubmitting ||
                              Number(item.issueNow) <= 0 ||
                              item.available_stock <= 0
                            }
                            style={{ fontSize: "12px", padding: "3px 10px" }}
                            title={item.available_stock <= 0 ? "No stock available" : `Issue up to ${remaining}`}
                          >
                            Issue
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueRequisition;
